import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Blockquote from '@tiptap/extension-blockquote';
import CodeBlock from '@tiptap/extension-code-block';
import Mention from '@tiptap/extension-mention';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import './NewsEditor.scss';
import Icon from '../Icon/Icon';
import { supabase } from '../../../supabase';
import { useKeyboardDetectionNoRoot } from '../../../helpers/hooks';


const NewsEditor = ({ value, onChange, className, placeholder, disableAutoHeading = false }) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },

            }),
            Link,
            Underline,
            Blockquote,
            CodeBlock,
            Image,
            Placeholder.configure({
                placeholder: ({ node }) => {
                    if (node.type.name === 'doc') {
                        return placeholder?.title || 'Title...';
                    }
                    if (node.type.name === 'heading') {
                        return 'News title...';
                    }
                    return placeholder?.body || 'Your amazing news comes here...';
                },
                emptyEditorClass: 'is-editor-empty',
                emptyNodeClass: 'is-empty',
            }),
            // Mention can be configured for advanced use
        ],
        content: value,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: 'news-editor-content',
                style: 'min-height: 180px; outline: none; font-size: 16px; padding: 0; background: transparent;',
            },
            handleKeyDown: (view) => {
                // Only apply auto-heading behavior if not disabled
                if (!disableAutoHeading) {
                    // Get the current content
                    const content = view.state.doc.textContent;
                    const firstLine = content.split('\n')[0];
                    // If we're on the first line and it's not already a heading
                    if (view.state.selection.$head.parentOffset === firstLine.length &&
                        !view.state.selection.$head.parent.type.name.includes('heading')) {
                        // Apply heading style to the first line
                        editor.commands.toggleHeading({ level: 1 });
                    }
                }
            }
        },
    });

    const fileInputRef = useRef(null);
    const { keyboardVisible, keyboardHeight } = useKeyboardDetectionNoRoot();


  

    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value || '', false);

            // Apply heading to first line if content exists
            if (value) {
                const firstLine = value.split('\n')[0];
                if (firstLine && !firstLine.includes('<h1>')) {
                    editor.commands.toggleHeading({ level: 1 });
                }
            }
        }
    }, [value, editor]);

    // const addImage = () => {
    //     const url = window.prompt('Enter image URL');
    //     if (url) {
    //         editor.chain().focus().setImage({ src: url }).run();
    //     }
    // };



    const uploadImageToSupabase = async (file) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        // const base64 = await fileToBase64(file);
        const { error } = await supabase.storage
            .from('news-content-images')
            .upload(
                `${fileName}`,
                file,
                { contentType: file.type }
            );
        if (error) throw error;

        const { data: publicUrlData } = supabase.storage
            .from('news-content-images')
            .getPublicUrl(`${fileName}`);

        return publicUrlData.publicUrl;
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];

        if (file) {
            try {
                const publicUrl = await uploadImageToSupabase(file);
                editor.chain().focus().setImage({ src: publicUrl }).run();
            } catch (err) {
                alert('Image upload failed');
                console.error(err);
            }
        }
    };

    // Check if editor is empty
    const isEditorEmpty = !value || value.trim() === '' || value === '<p></p>';

    return (
        <div className="news-editor-wrapper">
            <div className='px-8 pb-40' style={{ position: 'relative' }}>
                <EditorContent editor={editor} />
                {/* Always visible placeholder overlay */}
                {isEditorEmpty && (
                    <div className="news-editor-placeholder-overlay">
                        {/* Show title placeholder unless explicitly set to null/false */}
                        {placeholder?.title !== null && placeholder?.title !== false && (
                            <div className="placeholder-title">{placeholder?.title || 'Title...'}</div>
                        )}
                        <div className="placeholder-body">
                            {placeholder?.body || 'Write your amazing news here...'}
                        </div>
                    </div>
                )}
            </div>
            {editor && (
                <div className={`news-editor-toolbar py-4 ${className}`} style={{ bottom: keyboardVisible ? keyboardHeight : 0 }}>
                    <span onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'active' : ''}><b>B</b></span>
                    <span onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'active' : ''}><i>I</i></span>
                    {/* <span onClick={() => editor.chain().focus().toggleUnderline().run()} className={editor.isActive('underline') ? 'active' : ''}><u>U</u></span> */}
                    <span onClick={() => editor.chain().focus().toggleLink({ href: window.prompt('Enter URL') }).run()} className={editor.isActive('link') ? 'active' : ''}>
                        <Icon alias={"attachment"} width={20} height={20} color='#999999' />
                    </span>
                    <span onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive('heading', { level: 2 }) ? 'active' : ''}>H2</span>
                    <span onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={editor.isActive('heading', { level: 3 }) ? 'active' : ''}>H3</span>
                    <span onClick={() => editor.chain().focus().toggleBlockquote().run()} className={editor.isActive('blockquote') ? 'active' : ''}>
                        <Icon alias={"quotes"} width={20} height={20} color='#999999' />
                    </span>
                    <span onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'active' : ''}>
                        <Icon alias={"list"} width={20} height={20} color='#999999' />
                    </span>
                    <span onClick={() => editor.chain().focus().toggleCodeBlock().run()} className={editor.isActive('codeBlock') ? 'active' : ''}>
                        <Icon alias={'code'} width={20} height={20} color='#999999' />
                    </span>
                    <span onClick={() => {
                        const email = window.prompt('Enter email address');
                        if (email) {
                            editor.chain().focus().insertContent(`<a href="mailto:${email}">${email}</a>`).run();
                        }
                    }}>
                        <Icon alias={'email'} width={20} height={20} color='#999999' />
                    </span>
                    <span
                        onClick={() => editor.chain().focus().setHorizontalRule().run()}
                    >
                        <Icon alias={'density_large'} width={20} height={20} color='#999999' />
                    </span>
                    <span
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    >
                        <Icon alias={'image'} width={20} height={20} color='#999999' />
                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleImageUpload}
                        />
                    </span>
                </div>
            )}
        </div>
    )
}

export default NewsEditor; 